/**
 * @file picking-columns.tsx
 * @description Колонки очереди и журнала заданий на сборку для
 * `ResponsiveList` (phase19.31, шаг О4; макет (г)).
 *
 * Одно описание — таблица от 1024 и компактный список до 1024:
 *  - очередь: Заказ (номер задания подписью), Состав, Позиций, Склад и
 *    способ получения, На сборке (время), Статус и исполнитель;
 *    в списке — «Z-… ● В работе / состав / 1 из 4 позиций · Самовывоз · вы»;
 *  - журнал: тот же состав данных, что был (задание, заказ, статус,
 *    исполнитель, когда проведено, склад, позиций, недостач).
 * Карточка обезличена: без клиента, адреса и цен (W11).
 * На 1024–1279 часть колонок сворачивается второй линией в соседнюю
 * (`fold`): таблица влезает в блок без прокрутки (дефект приёмки
 * 2026-10-02 — журнал на 1024 был шире блока на 500px).
 * Время — по часовому поясу магазина (SC31).
 */

import { assigneeText, itemsFirstLine, itemsOneLine, itemsSecondLine } from "./picking-items-text";
import { PickingStatusBadge } from "./PickingStatusBadge";
import type { ListColumn } from "../../ui/responsive-list-types";
import { TwoLineCell } from "../../ui/TwoLineCell";
import { formatDeliveryMethod, type PickingTaskListItemDto } from "../../../lib/api-picking";
import { formatPickedPositions } from "../../../lib/picking-format";
import { pluralRu } from "../../../lib/plural";
import { formatShopTime } from "../../../lib/shop-time";

type Task = PickingTaskListItemDto;

/** Колонки очереди; `currentUserId` — подпись «вы» у своих заданий. */
export function pickingQueueColumns(currentUserId: string | null): ListColumn<Task>[] {
  return [
    {
      key: "order",
      header: "Заказ",
      width: "w-[168px]",
      cell: (t) => <TwoLineCell primary={t.orderNumber} secondary={t.number} mono />,
      compact: "title",
      compactCell: (t) => <span className="font-mono">{t.orderNumber}</span>,
    },
    {
      key: "items",
      header: "Состав",
      cell: (t) => <TwoLineCell primary={itemsFirstLine(t)} secondary={itemsSecondLine(t) || undefined} />,
      compact: "subtitle",
      compactCell: (t) => itemsOneLine(t),
    },
    {
      key: "positions",
      header: "Позиций",
      width: "w-[96px]",
      cell: (t) => `${t.pickedCount} из ${t.itemsCount}`,
      compact: "meta",
      compactCell: (t) => formatPickedPositions(t.pickedCount, t.itemsCount),
      fold: { into: "status", cell: (t) => formatPickedPositions(t.pickedCount, t.itemsCount) },
    },
    {
      key: "warehouse",
      header: "Склад",
      width: "w-[168px]",
      cell: (t) => <TwoLineCell primary={t.warehouse.name} secondary={formatDeliveryMethod(t.deliveryMethod)} />,
      compact: "meta",
      compactCell: (t) => formatDeliveryMethod(t.deliveryMethod),
    },
    {
      key: "createdAt",
      header: "На сборке",
      width: "w-[120px]",
      cell: (t) => <span className="text-on-surface-variant">{formatShopTime(t.createdAt)}</span>,
      fold: { into: "warehouse", cell: (t) => `на сборке с ${formatShopTime(t.createdAt)}` },
    },
    {
      key: "status",
      header: "Статус",
      width: "w-[156px]",
      cell: (t) => (
        <TwoLineCell primary={<PickingStatusBadge status={t.status} />} secondary={assigneeText(t, currentUserId)} />
      ),
      compact: "trailing",
      compactCell: (t) => <PickingStatusBadge status={t.status} />,
    },
    {
      key: "assignee",
      header: "Исполнитель",
      compactOnly: true,
      cell: (t) => assigneeText(t, currentUserId),
      compact: "meta",
    },
  ];
}

/** Колонки журнала закрытых заданий. */
export function pickingHistoryColumns(currentUserId: string | null): ListColumn<Task>[] {
  return [
    {
      key: "task",
      header: "Задание",
      width: "w-[168px]",
      cell: (t) => <TwoLineCell primary={t.number} secondary={t.orderNumber} mono />,
      compact: "title",
      compactCell: (t) => <span className="font-mono">{t.orderNumber}</span>,
    },
    {
      key: "status",
      header: "Статус",
      width: "w-[132px]",
      cell: (t) => <PickingStatusBadge status={t.status} />,
      compact: "trailing",
    },
    {
      key: "assignee",
      header: "Исполнитель",
      cell: (t) => assigneeText(t, currentUserId, "—"),
      compact: "meta",
    },
    {
      key: "finishedAt",
      header: "Проведено",
      width: "w-[132px]",
      cell: (t) => (t.finishedAt ? formatShopTime(t.finishedAt) : "—"),
      compact: "meta",
      compactCell: (t) => (t.finishedAt ? formatShopTime(t.finishedAt) : "дата не записана"),
    },
    {
      key: "warehouse",
      header: "Склад",
      width: "w-[160px]",
      cell: (t) => t.warehouse.name,
      compact: "subtitle",
      compactCell: (t) =>
        `${t.number} · ${t.itemsCount} ${pluralRu(t.itemsCount, ["позиция", "позиции", "позиций"])} · ${t.warehouse.name}`,
      fold: { into: "assignee" },
    },
    {
      key: "positions",
      header: "Позиций",
      width: "w-[96px]",
      align: "end",
      cell: (t) => t.itemsCount,
      fold: { into: "finishedAt", cell: (t) => `${t.itemsCount} ${pluralRu(t.itemsCount, ["позиция", "позиции", "позиций"])}` },
    },
    {
      key: "shortages",
      // «Недостачи», а не «Недостач»: родительный падеж читался как обрезанное
      // слово (дефект приёмки 2026-10-02, журнал на 1024).
      header: "Недостачи",
      width: "w-[104px]",
      align: "end",
      cell: (t) => shortageCount(t.shortageItemsCount ?? 0),
      compact: "trailing",
      compactCell: (t) => ((t.shortageItemsCount ?? 0) > 0 ? shortageCount(t.shortageItemsCount ?? 0, true) : null),
    },
  ];
}

/** Недостачи: число красным, ноль — прочерк. */
function shortageCount(count: number, prefix = false) {
  if (count <= 0) return <span className="text-on-surface-variant">—</span>;
  return <span className="text-[var(--status-error-fg)] tabular-nums">{prefix ? `недостач: ${count}` : count}</span>;
}
